import { FormExtractionAssistant } from "@/components/form_extraction/FormExtractionAssistant";
import { PlanReviewAssistant } from "@/components/plan_review/PlanReviewAssistant";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { PermitTrackingPanel } from "@/components/permit_suite/PermitTrackingPanel";
import {
  isPermitHubSection,
  tabForPermitHubSection,
  type PermitSuiteTab,
} from "@/components/permit_suite/permitSuiteTabs";
import { usePermitSuiteNavigation } from "@/context/PermitSuiteNavigationContext";
import { useUser } from "@/context/UserProvider";
import { canAccessPermitTab } from "@/lib/permitSuiteAccess";
import { cn } from "@/lib/utils";
import type { ActiveSection } from "@/types/appNavigation";

type PermitSuiteWorkspaceProps = {
  entrySection?: ActiveSection;
};

function workspaceTitle(tab: PermitSuiteTab): string {
  switch (tab) {
    case "pipeline":
      return "Permit Suite";
    case "plan_review":
      return "Plan Review";
    case "assistant":
      return "Permit Assistant";
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
}

function workspaceSubtitle(tab: PermitSuiteTab): string {
  switch (tab) {
    case "pipeline":
      return "Track permits through your review pipeline.";
    case "plan_review":
      return "Analyze submitted site plans against your code.";
    case "assistant":
      return "Extract and validate permit application fields.";
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
}

export function PermitSuiteWorkspace({ entrySection = "permit_suite" }: PermitSuiteWorkspaceProps) {
  const { currentUser } = useUser();
  const { focusRequest, clearPermitApplicationFocus } = usePermitSuiteNavigation();
  const section = isPermitHubSection(entrySection) ? entrySection : "permit_suite";
  const tab = tabForPermitHubSection(section);
  const fullBleedTab = tab === "pipeline" || tab === "plan_review";

  // Callers are expected to gate on canAccessPermitTab before mounting this
  // component. This is a last line of defense: a caller that requests a tab
  // the user's flags don't grant gets nothing rendered, never another
  // product's name or an empty pane with a header for a module the team
  // didn't buy.
  if (!canAccessPermitTab(currentUser, tab)) return null;

  return (
    <div className={cn(moduleWorkspaceShellClass, "flex-1 min-h-0")} data-testid="permit-suite-workspace">
      <ModuleWorkspaceHeader
        title={workspaceTitle(tab)}
        subtitle={workspaceSubtitle(tab)}
        actions={<MunicipalitySwitcher />}
        density="compact"
      />

      <ModuleWorkspaceBody
        className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", fullBleedTab && "!p-0")}
        contentClassName="flex h-full min-h-0 flex-1 flex-col"
      >
        {tab === "pipeline" ? (
          <PermitTrackingPanel
            focusRequest={focusRequest}
            onFocusRequestHandled={clearPermitApplicationFocus}
          />
        ) : null}
        {tab === "plan_review" ? <PlanReviewAssistant embedded /> : null}
        {tab === "assistant" ? <FormExtractionAssistant embedded /> : null}
      </ModuleWorkspaceBody>
    </div>
  );
}
